/**
 * Frontend half of the discussion-comment `platform` vocabulary.
 *
 * `lambda/common/platforms.py` owns the platform registry; the platforms it
 * flags `has_comments` are the ones whose codes comment_ingest writes to
 * `article_comments.platform` and the discussion-summary Lambda carries into
 * `discussion.top_comments[].platform`, which the reader renders. The union
 * used to be spelled inline in `types.ts`, and DiscussionSection kept its own
 * code→label ternary. This module owns the vocabulary and the display labels;
 * `backend/tests/test_mirror_parity.py` pins it to the registry's
 * has-comments set.
 */
export const COMMENT_PLATFORMS = ['hn', 'reddit', 'lobsters'] as const;

export type CommentPlatform = (typeof COMMENT_PLATFORMS)[number];

const COMMENT_PLATFORM_LABELS: Record<CommentPlatform, string> = {
  hn: 'HN',
  reddit: 'Reddit',
  lobsters: 'Lobsters',
};

function isCommentPlatform(value: string): value is CommentPlatform {
  return (COMMENT_PLATFORMS as readonly string[]).includes(value);
}

/**
 * The label shown next to a comment pick. Falls back to the raw code so a
 * platform enabled server-side before this mirror learns its label still
 * renders identifiably rather than blank.
 */
export function commentPlatformLabel(platform: string): string {
  return isCommentPlatform(platform) ? COMMENT_PLATFORM_LABELS[platform] : platform;
}
